import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cube } from '../ui/CountingCubes';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X } from 'lucide-react';

const AnimatedAddRemove = ({ exercise, onAnswer }) => {
  const isAdd = exercise.type === 'animated-add';
  const { start, options } = exercise;
  const change = isAdd ? exercise.add : exercise.remove;
  const correctAnswer = isAdd ? start + change : start - change;
  
  const [phase, setPhase] = useState('initial'); // 'initial', 'animating', 'question', 'answered'
  const [currentCount, setCurrentCount] = useState(start);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    // Reset and start animation sequence
    setPhase('initial');
    setCurrentCount(start);
    setSelectedAnswer(null);
    setIsCorrect(false);

    // Start animation after a delay
    const animTimer = setTimeout(() => {
      setPhase('animating');
    }, 1000);

    return () => clearTimeout(animTimer);
  }, [exercise, start]);

  useEffect(() => {
    if (phase === 'animating') {
      // Animate the count change
      const updateTimer = setTimeout(() => {
        setCurrentCount(correctAnswer);
        setTimeout(() => {
          setPhase('question');
        }, 800);
      }, 500);
      
      return () => clearTimeout(updateTimer);
    }
  }, [phase, correctAnswer]);

  const handleAnswer = (answer) => {
    if (phase !== 'question') return;
    
    setSelectedAnswer(answer);
    const correct = answer === correctAnswer;
    setIsCorrect(correct);
    setPhase('answered');

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getButtonClass = (option) => {
    if (phase !== 'answered') {
      return 'btn-number-default hover:border-primary hover:scale-105';
    }
    if (option === correctAnswer) {
      return 'btn-number-correct';
    }
    if (option === selectedAnswer && !isCorrect) {
      return 'btn-number-incorrect animate-shake';
    }
    return 'btn-number-default opacity-50';
  };

  const questionText = isAdd ? QUESTIONS['animated-add'] : QUESTIONS['animated-remove'];
  const actionText = isAdd 
    ? `+${change} пришёл${change > 1 ? 'и' : ''}` 
    : `-${change} ушёл${change > 1 ? 'и' : ''}`;

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  // Generate cube positions for animation
  const cubePositions = Array.from({ length: Math.max(start, correctAnswer) }, (_, i) => i);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Question */}
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-4 text-center"
      >
        {phase === 'question' || phase === 'answered' ? questionText : 'Смотри внимательно!'}
      </motion.h2>

      {/* Action indicator */}
      <AnimatePresence>
        {phase === 'animating' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className={`mb-4 px-4 py-2 rounded-full font-display font-bold ${
              isAdd ? 'bg-correct-light text-success' : 'bg-secondary-light text-secondary-foreground'
            }`}
          >
            {actionText}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animated Cubes Area */}
      <motion.div
        className="mb-8 p-6 bg-card rounded-2xl border-2 border-border shadow-card min-w-[200px] min-h-[100px] flex items-center justify-center"
      >
        <div className="flex gap-2 flex-wrap justify-center max-w-[180px]">
          <AnimatePresence mode="popLayout">
            {cubePositions.slice(0, currentCount).map((i) => (
              <motion.div
                key={i}
                initial={isAdd && i >= start ? { x: 100, opacity: 0, scale: 0.5 } : { scale: 1 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                exit={{ x: -100, opacity: 0, scale: 0.5 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <Cube index={i} size="md" animate={false} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Answer Options */}
      <AnimatePresence>
        {(phase === 'question' || phase === 'answered') && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            {options.map((option, index) => (
              <motion.button
                key={option}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1, type: 'spring' }}
                onClick={() => handleAnswer(option)}
                disabled={phase === 'answered'}
                className={getButtonClass(option)}
              >
                {phase === 'answered' && option === correctAnswer && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center"
                  >
                    <Check className="w-4 h-4 text-card" />
                  </motion.span>
                )}
                {phase === 'answered' && option === selectedAnswer && !isCorrect && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-card" />
                  </motion.span>
                )}
                {option}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feedback */}
      <AnimatePresence>
        {phase === 'answered' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedAddRemove;
